'use es6';
/*
 * Unified Layouts are layouts along with some metadata about content associated with the layout.
 */

/**
 * SHAPE
 *
  {
    byId: {
      101: {
        layout: {
          id: 101,
          label: 'Render Rewrite Test',
          portalId: 99209952,
          purchased: false,
          templateType: 4,
          updated: 1467386707577,
        },
        layoutData {
          rootWidgetId: 'widget_1',
          widgets: {
            widget_1: {type: 'cell', w: 12, x: 0, rows: ['row_1', 'row_2']},
            widget_2: {type: 'rich_text', x: 0, w: 6, rows: []},
            widget_3: {type: 'rich_text', x: 6, w: 6, rows: []},
            widget_4: {type: 'rich_text', x: 0, w: 12, rows: []},
          },
          rows: {
            row_1: ['widget_2', 'widget_3'],
            row_2: ['widget_4'],
          },
        },
      }
    },
    isFetchingById: {
      101: false,
    },
    fetchErrorById: {
      101: null,
    },
    layoutsToCloneFrom: {
      "hubspot-default-2-column": UnifiedLayout
    }
  }
*/

import layoutDataReducer from 'DesignData/reducers/unifiedLayouts/LayoutData';
import cosIndexConnections from 'DesignData/reducers/unifiedLayouts/CosIndexConnections';
import SeamlessImmutable from 'seamless-immutable';
import { combineReducers } from 'redux';
import undoable from 'redux-undo';
import { without } from 'underscore';
import { omit, isEqual } from 'underscore';
import { UnifiedLayout } from 'DesignData/models/layouts';
import { UNIFIED_LAYOUT_FETCH, LAYOUT_CREATE_FROM_FILE, LAYOUT_BUFFER_SAVE, LAYOUT_PROPERTIES_EDIT, LAYOUT_PUBLISH, LAYOUT_TOGGLE_EMAIL_TYPE, LAYOUT_DELETE, LAYOUT_MOVE_RENAME, LAYOUT_EDIT_UNDO, LAYOUT_EDIT_REDO, LAYOUT_CLEAR_UNDO_HISTORY, LAYOUT_REVERT, LAYOUT_DISCARD_BUFFER, LAYOUT_TO_CLONE_FROM_FETCH, LAYOUT_RENAME, LAYOUT_UPDATE_FOLDER, LAYOUT_TOGGLE_IS_AVAILABLE_FOR_NEW_CONTENT, LAYOUT_LOCALIZATION_DELETE_LANGUAGE, LAYOUT_LOCALIZATION_CHANGE_MASTER_LANGUAGE, LAYOUT_LOCALIZATION_OPT_IN, LAYOUT_LOCALIZATION_ADD_LANGUAGE, LAYOUT_WIDGET_DELETE_CONFIRM, LAYOUT_WIDGET_DELETE_REVERT, LAYOUT_WIDGET_GROUP_TYPE_TRANSFORM_CONFIRM, LAYOUT_WIDGET_GROUP_TYPE_TRANSFORM_REVERT } from 'DesignData/actions/ActionTypes';
import { fixSectionDelimiters } from 'DesignData/lib/normalization/layouts/fixes/sectionDelimiters';
import { fixNonUniqueWidgetIds } from 'DesignData/lib/normalization/layouts/fixes/duplicateWidgetIds';
import { EMPTY_OBJECT } from 'DesignData/lib/object';
import { HISTORY_LIMIT } from 'DesignData/constants/UndoRedo';
const LAYOUT_FIELDS_TO_OMIT_FROM_STATE = Object.freeze(['layoutData', 'body']);
/**
 * Helper to update only change info for optimistic interface.
 */

function setChangeInfo(state, action, hasBufferedChanges) {
  const {
    layout
  } = action;

  if (!layout) {
    return state;
  }

  if (!(state instanceof UnifiedLayout)) {
    return null;
  }

  const {
    meta,
    updated,
    updatedBy
  } = layout;
  const oldAssociatedContent = SeamlessImmutable.getIn(state, ['meta', 'associatedContent']);
  const newAssociatedContent = meta && meta.associatedContent;
  const changeInfo = {
    updated,
    updatedBy,
    meta: Object.assign({}, meta, {
      hasBufferedChanges,
      associatedContent: isEqual(newAssociatedContent, oldAssociatedContent) ? oldAssociatedContent : newAssociatedContent
    })
  };
  return SeamlessImmutable.merge(state, changeInfo, {
    deep: true
  });
}
/**
 * Helper for merging layouts.
 */


function mergeLayout(state, data, hasBufferedChanges) {
  if (!data) {
    return state;
  }

  if (!(state instanceof UnifiedLayout)) {
    return null;
  }

  return state.merge(data, {
    deep: true
  }).without(LAYOUT_FIELDS_TO_OMIT_FROM_STATE).setIn(['meta', 'hasBufferedChanges'], hasBufferedChanges);
}
/**
 * layout
 * Keeps state for layout properties except for the layout tree. layoutData does that.
 *
 * Layout object and its data, however not it's tree. That is in layoutData.
 */


function layoutReducer(state = null, action) {
  switch (action.type) {
    case UNIFIED_LAYOUT_FETCH:
    case LAYOUT_CREATE_FROM_FILE:
      {
        const {
          layout
        } = action;

        if (!layout) {
          return state;
        }

        return new UnifiedLayout(layout).without(LAYOUT_FIELDS_TO_OMIT_FROM_STATE);
      }

    case LAYOUT_BUFFER_SAVE:
      {
        return setChangeInfo(state, action, true);
      }

    case LAYOUT_PUBLISH:
      {
        return setChangeInfo(state, action, false);
      }
    // Affects only layout

    case LAYOUT_MOVE_RENAME:
      {
        return mergeLayout(state, action.layout, false);
      }
    // Affects layoutData

    case LAYOUT_DISCARD_BUFFER:
    case LAYOUT_REVERT:
      {
        return mergeLayout(state, action.layout, false);
      }

    case LAYOUT_TOGGLE_EMAIL_TYPE:
      {
        const {
          generator
        } = action;
        return mergeLayout(state, {
          generator
        }, true);
      }

    case LAYOUT_PROPERTIES_EDIT:
      {
        const {
          edits
        } = action;
        return mergeLayout(state, edits, true);
      }

    case LAYOUT_RENAME:
      {
        const {
          label
        } = action;

        if (label) {
          return mergeLayout(state, {
            label
          }, true);
        }

        return state;
      }

    case LAYOUT_UPDATE_FOLDER:
      {
        const {
          file
        } = action;

        if (file) {
          return mergeLayout(state, {
            folderId: file.folderId,
            path: file.path,
            generatedTemplatePath: file.generatedTemplatePath
          }, true);
        }

        return state;
      }

    case LAYOUT_TOGGLE_IS_AVAILABLE_FOR_NEW_CONTENT:
      {
        const isAvailableForNewContent = state ? !state.isAvailableForNewContent : true;
        return mergeLayout(state, {
          isAvailableForNewContent
        }, true);
      }

    case LAYOUT_LOCALIZATION_OPT_IN:
      {
        const {
          langCode: masterLanguage
        } = action;
        return mergeLayout(state, {
          masterLanguage,
          languages: [masterLanguage]
        }, true);
      }

    case LAYOUT_LOCALIZATION_ADD_LANGUAGE:
      {
        const {
          langCode
        } = action;
        const {
          languages = []
        } = state;
        return mergeLayout(state, {
          languages: [...languages, langCode]
        }, true);
      }

    case LAYOUT_LOCALIZATION_CHANGE_MASTER_LANGUAGE:
      {
        const {
          langCode: masterLanguage
        } = action;
        const {
          languages = []
        } = state;
        return mergeLayout(state, {
          masterLanguage,
          languages: without(languages, masterLanguage)
        }, true);
      }

    case LAYOUT_LOCALIZATION_DELETE_LANGUAGE:
      {
        const {
          langCode
        } = action;
        const {
          languages = []
        } = state;
        return mergeLayout(state, {
          languages: without(languages, langCode)
        }, true);
      }

    default:
      return state;
  }
}
/**
 * Combines reducers for layouts and layout trees.
 */
// const layoutObjectsSubreducer = combineReducers({
//   layout: layoutReducer,
//   layoutData: layoutDataReducer,
// });


const layoutObjectsSubreducer = (state = EMPTY_OBJECT, action) => {
  // layout
  const layout = layoutReducer(state.layout, action);
  state = SeamlessImmutable.set(state, 'layout', layout); // layoutData
  // Apply data fixes as needed.

  let layoutData = layoutDataReducer(state.layoutData, action);

  if (layout && layoutData.rootWidgetId) {
    // Invalid section delimiters
    const delimiterFixedData = fixSectionDelimiters(layoutData, layout.templateType);

    if (delimiterFixedData !== layoutData) {
      layoutData = delimiterFixedData;
    } // Non-unique widget ids


    layoutData = fixNonUniqueWidgetIds(layoutData);
  }

  state = SeamlessImmutable.set(state, 'layoutData', layoutData);
  return state;
};
/*
 * Wraps `layoutObjectsSubreducer` with redux-undo.
 */


const undoableLayoutObjectsSubreducer = undoable(layoutObjectsSubreducer, {
  limit: HISTORY_LIMIT,
  undoType: LAYOUT_EDIT_UNDO,
  redoType: LAYOUT_EDIT_REDO,
  clearHistoryType: LAYOUT_CLEAR_UNDO_HISTORY,

  filter(action, currentState, previousHistory) {
    if (action.layoutId !== currentState.layout.id) {
      // Only listen to self
      return false;
    }

    switch (action.type) {
      // Return false to replace the last event in history.
      case UNIFIED_LAYOUT_FETCH:
        {
          if (previousHistory.present.layout) {
            // Refetch
            return false;
          }

          return true;
        }

      case LAYOUT_MOVE_RENAME:
      case LAYOUT_PUBLISH:
      case LAYOUT_BUFFER_SAVE:
        {
          return false;
        }

      default:
        return true;
    }
  }

});
/**
 * Manages state for layouts and layout trees by layout id.
 * Uses redux-undo.
 */

function byId(state = EMPTY_OBJECT, action) {
  switch (action.type) {
    case LAYOUT_DELETE:
      {
        const {
          layoutId,
          deleted
        } = action;

        if (!deleted) {
          return state;
        }

        return state.without(layoutId);
      }

    case LAYOUT_UPDATE_FOLDER:
      {
        const {
          layoutId
        } = action;
        if (!layoutId) return state;
        const childState = undoableLayoutObjectsSubreducer(state[layoutId], action);
        return state.set(layoutId, childState);
      }

    case LAYOUT_WIDGET_GROUP_TYPE_TRANSFORM_REVERT:
    case LAYOUT_WIDGET_DELETE_REVERT:
      {
        const {
          layoutId,
          snapshot
        } = action;

        if (layoutId && snapshot && snapshot !== EMPTY_OBJECT) {
          return SeamlessImmutable.set(state, layoutId, snapshot);
        }

        return state;
      }

    default:
      {
        const {
          layoutId
        } = action;

        if (!layoutId) {
          return state;
        }

        const childState = undoableLayoutObjectsSubreducer(state[layoutId], action);
        return state.set(layoutId, childState);
      }
  }
}
/**
 * isFetchingById
 *
 * Signifies the pending status of fetches by layout id.
 * True if an XHR request to /layouts/unifed-get is pending, false otherwise.
 */


function isFetchingById(state = EMPTY_OBJECT, action) {
  switch (action.type) {
    case UNIFIED_LAYOUT_FETCH:
      {
        const {
          layoutId,
          layout,
          error
        } = action;
        const isFetching = !(layout || error);

        if (state[layoutId] === isFetching) {
          return state;
        }

        return state.merge({
          [layoutId]: isFetching
        });
      }

    case LAYOUT_DELETE:
      {
        const {
          layoutId,
          deleted
        } = action;

        if (!deleted) {
          return state;
        }

        return state.without(layoutId);
      }

    default:
      return state;
  }
}
/**
 * fetchErrorById
 *
 * Last XHR fetch error per layout by layout id.
 * State cannot be immutable since instances of Error cannot be immuted.
 */


function fetchErrorById(state = {}, action) {
  switch (action.type) {
    case UNIFIED_LAYOUT_FETCH:
      {
        const {
          layoutId,
          error
        } = action;

        if (error === undefined && state[layoutId] === undefined) {
          return Object.assign({}, state, {
            [layoutId]: null
          });
        }

        if (state[layoutId] === error) {
          return state;
        }

        return Object.assign({}, state, {
          [layoutId]: error || null
        });
      }

    case LAYOUT_DELETE:
      {
        const {
          layoutId,
          deleted
        } = action;

        if (deleted === true) {
          return state;
        }

        return omit(state, layoutId);
      }

    default:
      return state;
  }
}
/*
 * layoutsToCloneFrom
 */


function layoutsToCloneFrom(state = EMPTY_OBJECT, action) {
  switch (action.type) {
    case LAYOUT_TO_CLONE_FROM_FETCH:
      {
        const {
          layout
        } = action;

        if (!layout) {
          return state;
        }

        const model = new UnifiedLayout(layout);
        return state.set(model.id, model);
      }

    default:
      return state;
  }
}
/*
 * isPublishingById
 *
 * Signifies the pending status of publishes by layout id.
 */


function isPublishingById(state = EMPTY_OBJECT, action) {
  switch (action.type) {
    case LAYOUT_PUBLISH:
      {
        const {
          layoutId,
          layout,
          error
        } = action;
        const isPublishing = !(layout || error);

        if (state[layoutId] === isPublishing) {
          return state;
        }

        return state.merge({
          [layoutId]: isPublishing
        });
      }

    default:
      return state;
  }
}
/*
 * isSavingById
 *
 * Signifies the pending status of saves by layout id.
 */


function isSavingById(state = EMPTY_OBJECT, action) {
  switch (action.type) {
    case LAYOUT_BUFFER_SAVE:
      {
        const {
          layoutId,
          layout,
          error
        } = action;
        const isSaving = !(layout || error);

        if (state[layoutId] === isSaving) {
          return state;
        }

        return state.merge({
          [layoutId]: isSaving
        });
      }

    default:
      return state;
  }
}
/*
 * saveErrorById
 *
 * Records errors on save by layout id
 */


function saveErrorById(state = EMPTY_OBJECT, action) {
  switch (action.type) {
    case LAYOUT_BUFFER_SAVE:
      {
        const {
          layoutId,
          error
        } = action;
        return state.merge({
          [layoutId]: error
        });
      }

    default:
      return state;
  }
}

const [
/**
 * Is populated for widgets that need confirmation before deletion.
 */
stagedWidgetsForDeleteConfirmation,
/**
 * Is populated for widgets groups that need confirmation before changing type.
 */
stagedWidgetsForGroupTypeTransformConfirmation] = [LAYOUT_WIDGET_DELETE_CONFIRM, LAYOUT_WIDGET_GROUP_TYPE_TRANSFORM_CONFIRM].map(actionType => {
  return function (state = EMPTY_OBJECT, action) {
    switch (action.type) {
      case actionType:
        {
          const {
            layoutId,
            widgetIds,
            data
          } = action;

          if (widgetIds && widgetIds.length) {
            return SeamlessImmutable.set(state, layoutId, {
              widgetIds,
              data
            });
          }

          return state[layoutId] ? SeamlessImmutable.without(state, layoutId) : state;
        }

      default:
        return state;
    }
  };
});
/*
 * Export
 */

export default combineReducers({
  byId,
  isFetchingById,
  fetchErrorById,
  layoutsToCloneFrom,
  isPublishingById,
  isSavingById,
  saveErrorById,
  cosIndexConnections,
  stagedWidgetsForDeleteConfirmation,
  stagedWidgetsForGroupTypeTransformConfirmation
});